import {PathComponent} from "index";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {Action, BadgeSize, ListItemChange, OnListItemsListener, UserCache, UserProfilePhoto} from "../shared/types";
import {Auth, getAuth} from "@firebase/auth";
import {User} from "../shared/entities";
import {
  AccountBoxOutlined,
  AddOutlined,
  AnalyticsOutlined,
  AutoAwesomeOutlined,
  CallOutlined,
  ChatBubbleOutlineOutlined,
  CloseOutlined,
  DynamicFormOutlined,
  InboxOutlined,
  LanguageOutlined,
  NotificationsOutlined,
  OpenInFullOutlined,
  SearchOutlined,
  SendOutlined,
  SettingsOutlined,
  SmsOutlined,
  ViewAgendaOutlined
} from "@mui/icons-material";
import {MessageCampaignsFragment} from "./campaigns/messages/MessageCampaignsFragment";
import {Tabs, TabsColorScheme, TabsContainer} from "../shared/TabsContainer";
import {StyledBadge2, StyledBoxRow, StyledEmpty, StyledSpan} from "../shared/StyledComponents";
import {
  BORDER_RADIUS,
  DIVIDER,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_WIDTH_SM,
  PAGE_FRAGMENT_XXXLG_WIDTH,
  PD_LG,
  PD_MD,
  PD_SM,
  PD_XLG,
  PD_XXSM,
  PD_XXXLG,
  SZ_MD,
  SZ_SM
} from "../shared/dimens";
import {ContactsFragment} from "./contacts/ContactsFragment";
import {
  AccountProfileContextTrait,
  Notification,
  Notifications,
  Page,
  Pages,
  PracticePageProfile
} from "../libwyze-shared/types";
import {BaseApp} from "../shared/BaseApp";
import {HomeFragment} from "./home/HomeFragment";
import {SitesFragment} from "./sites/SitesFragment";
import {Button, ButtonBase, Card, IconButton, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import {AskWyzeFragment} from "@/askwyze-shared/askwyze/AskWyzeFragment";
import {AskWyzeMessagesFragment} from "@/askwyze-shared/askwyze/AskWyzeMessagesFragment";
import {PathProps} from "../shared";
import {AskWyzeViewMode} from "@/askwyze-shared/askwyze/AskWyzeView";
import {
  ChatMessages,
  ChatThread,
  ChatThreads,
  MessageFactory,
  ParticipantKey,
  ParticipantType
} from "@/app-modules/messages/chat/types";
import {MessagesContextTrait} from "@/app-modules/messages/types";
import {sleep} from "../shared/sleep";
import {Features} from "./features";
import {COLOR_PRIMARY} from "./theme";
import {App} from "./App";
import {AppTabsContainer} from "../shared/AppTabsContainer";
import {FormsFragment} from "@/app-modules/forms/FormsFragment";
import {appType} from "../libwyze-common/types";
import React, {Component, ReactElement, useEffect, useRef} from "react";
import {findIcon} from "../shared/icons";
import {EditProviderMessagingHelper} from "@/libwyze-shared/components/EditProviderMessagingHelper";
import {NotificationsContextTrait, NotificationsSource} from "@/libwyze-shared/types";
import {PracticeSelect} from "../libwyze-shared/components/PracticeSelect";
import {UserFragment} from "../shared/UserFragment";
import {PlaceholderFragment} from "../shared/PlaceholderFragment";
import {colorRed} from "../shared/colors";
import {SettingsFragment} from "./settings/SettingsFragment";
import {NotificationsFragment} from "../shared/NotificationsFragment";
import {StudioFragment} from "@/vantage-studio-shared/studio/StudioFragment";
import {ChannelsFragment} from "./campaigns/channels/ChannelsFragment";

enum Tab {
  CONTACTS = "contacts",
  INBOX = "inbox",
  CALLS = "calls",
  CHANNELS = "channels",
  MESSAGING = "messaging",
  SITES = "editor",
  STUDIO = "studio",
  ANALYTICS = "analytics",
  MESSAGES = "messages",
  FORMS = "forms",
  SETTINGS = "settings",
  NOTIFICATIONS = "notifications",
}

const TABS: Tabs<Tab> = {
  items: [
    {
      type: Tab.CONTACTS,
      path: "contacts",
      text: "Contacts",
      iconType: AccountBoxOutlined,
      render: path => <ContactsFragment path={path}/>,
    },
    // {
    //   type: Tab.INBOX,
    //   path: "inbox",
    //   text: "Inbox",
    //   iconType: InboxOutlined,
    //   render: path => <PlaceholderFragment path={path}/>,
    // },
    // {
    //   type: Tab.CALLS,
    //   path: "calls",
    //   text: "Calls",
    //   iconType: CallOutlined,
    //   render: path => <PlaceholderFragment path={path}/>,
    // },
    // {
    //   type: Tab.CHANNELS,
    //   path: "channels",
    //   text: "Channels",
    //   iconType: ViewAgendaOutlined,
    //   nestedPaths: ChannelsFragment.nestedPaths(),
    //   render: path => <ChannelsFragment path={path}/>,
    // },
    {
      type: Tab.MESSAGING,
      path: "messaging",
      text: "Messages",
      iconType: SendOutlined,
      nestedPaths: MessageCampaignsFragment.nestedPaths(),
      render: path => <MessageCampaignsFragment path={path}/>,
    },
    {
      type: Tab.SITES,
      path: "sites",
      text: "Sites",
      iconType: LanguageOutlined,
      nestedPaths: SitesFragment.nestedPaths(),
      render: path => <SitesFragment path={path}/>,
    },
    {
      type: Tab.STUDIO,
      path: "studio",
      text: "Studio",
      iconType: AutoAwesomeOutlined,
      render: path => <StudioFragment path={path}/>,
    },
    {
      type: Tab.ANALYTICS,
      path: "analytics",
      text: "Analytics",
      iconType: AnalyticsOutlined,
      render: path => <HomeFragment path={path}/>,
    },
    // {
    //   type: Tab.MESSAGES,
    //   path: "messages",
    //   text: "Messages",
    //   hidden: true,
    //   iconType: ChatBubbleOutlineOutlined,
    //   nestedPaths: AskWyzeMessagesFragment.nestedPaths(),
    //   render: (path: PathProps) => <AskWyzeFragment path={path}/>,
    // },
    {
      type: Tab.FORMS,
      path: "forms",
      text: "Forms",
      iconType: DynamicFormOutlined,
      nestedPaths: FormsFragment.nestedPaths(),
      render: path => <FormsFragment path={path} nestedPageWithSidebarBasePath={"forms"}/>,
    },
    {
      type: Tab.SETTINGS,
      path: "settings",
      text: "Settings",
      hidden: true,
      iconType: SettingsOutlined,
      nestedPaths: SettingsFragment.nestedPaths(),
      render: path => <SettingsFragment path={path}/>,
    },
    {
      type: Tab.NOTIFICATIONS,
      path: "notifications",
      text: "Notifications",
      hidden: true,
      iconType: NotificationsOutlined,
      nestedPaths: NotificationsFragment.nestedPaths(),
      render: path => <NotificationsFragment path={path}/>,
    },
  ],
  titleElement: <StyledBoxRow style={{alignItems: "center"}}>
    <img src={"/images/dentwyze_vantage_logotype.svg"} style={{height: 48, margin: PD_MD}}/>
  </StyledBoxRow>,
  containerId: "main",
}

function getPracticePageNameTitleSuffix(): string {
  const practicePageId = App.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
  if (practicePageId) {
    const name = Pages.getInstance().getListItem(practicePageId)?.practicePageProfile?.basicInfo.name;
    return name ? ` - ${name}` : "";
  }
  return "";
}

const vantageLogo = <img src={"/images/dentwyze_vantage_logotype.svg"}
                         style={{height: 48, marginBottom: -4, marginLeft: -12}}/>;

const WhatsappOutlined = findIcon("whatsapp");

function SearchAccessory(props: { path: PathProps }): ReactElement {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const md = useMediaQuery(theme.breakpoints.up("md"));
  if (!sm) {
    return <StyledSpan/>;
  }
  return <div style={{
    maxWidth: PAGE_FRAGMENT_WIDTH_SM,
    background: theme.palette.grey[100],
    borderRadius: BORDER_RADIUS,
    width: "100%",
    flexGrow: 1,
    display: "flex",
    justifyContent: "center"
  }}>
    <Button
      onClick={() => {
      }}
      variant="outlined"
      style={{padding: 0, width: "100%"}}>
      <StyledBoxRow style={{padding: `${PD_XXSM}px ${PD_LG}px`}}>
        <SearchOutlined/>
        {md ? <Typography>Search</Typography> : null}
      </StyledBoxRow>
    </Button>
  </div>;
}

function ProviderJewels(props: { path: PathProps }): ReactElement {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  return md ? <>
      <Tooltip title={"SMS"}>
        <Button
          style={{minWidth: 0, padding: PD_SM}}
          onClick={(event) => EditProviderMessagingHelper.addProviderSms()}>
          <SmsOutlined/>
        </Button>
      </Tooltip>
      <Tooltip title={"WhatsApp"}>
        <Button
          style={{minWidth: 0, padding: PD_SM}}
          onClick={(event) => EditProviderMessagingHelper.addProviderWhatsApp()}>
          <WhatsappOutlined/>
        </Button>
      </Tooltip>
      <Tooltip title={"Settings"}>
        <Button
          variant={props.path.location.pathname.startsWith("/settings") ? "contained" : null}
          style={{minWidth: 0, padding: PD_SM}}
          onClick={() => props.path.navigate("/settings")}>
          <SettingsOutlined/>
        </Button>
      </Tooltip>
    </>
    : null;
}

class NotificationsJewel extends Component<{ path: PathProps }, {
  source?: NotificationsSource,
  count?: number,
}> {
  constructor(props: { path: PathProps }, context: any) {
    super(props, context);
    this.state = {}
  }

  setSource(source: NotificationsSource | null): void {
    this.setState({
      source,
    })
  }

  setCount(count: number): void {
    this.setState({
      count,
    });
  }

  render(): ReactElement {
    const source = this.state.source;
    const count = this.state.count;
    return <Tooltip title={"Notifications"}>
      <Button
        variant={this.props.path.location.pathname.startsWith("/notifications") ? "contained" : null}
        style={{position: "relative", overflow: "auto", minWidth: 0, padding: PD_SM}}
        onClick={(event) => source?.showPopup(event.target as HTMLElement)}>
        <NotificationsOutlined/>
        {count > 0
          ? <StyledBadge2
            selected
            backgroundColor={colorRed}
            boxStyle={{position: "absolute", top: 0, right: 0}}
            size={BadgeSize.XSMALL}
          >
            {count}
          </StyledBadge2>
          : null}
      </Button>
    </Tooltip>;
  }
}

function MainTitleBar(props: {
  auth?: Auth,
  path: PathProps,
  practices: PracticePageProfile[],
  selectedPractice: PracticePageProfile,
  setSelectedPractice: (profile: PracticePageProfile) => void
}): ReactElement {
  const notificationsJewelRef = useRef<NotificationsJewel>();
  useEffect(() => {
    const notificationsSource = BaseApp.CONTEXT.getTrait<NotificationsContextTrait>().getNotificationsSource();
    const observer = new class implements OnListItemsListener<Notification> {
      onItemChanged(item: Notification, change: ListItemChange): void {
        notificationsJewelRef.current?.setCount(notificationsSource.getNotificationsCount());
      }
    }
    notificationsJewelRef.current?.setSource(notificationsSource);
    notificationsSource?.registerObserver(observer);
    return () => {
      notificationsJewelRef.current?.setSource(null);
      notificationsSource?.unregisterObserver(observer);
    }
  }, []);
  const theme = useTheme();
  const md = theme.breakpoints.up("md");
  const titleElement = vantageLogo;
  return <StyledBoxRow style={{
    flexGrow: 1,
    maxWidth: PAGE_FRAGMENT_XXXLG_WIDTH,
    alignItems: "center",
  }}>
    <a href={"/"}>
      <StyledBoxRow style={{alignItems: "center"}}>
        <img src={md ? `/images/${appType}_logotype.svg` : `/images/${appType}_logotype_sm.svg`}
             style={{display: "block", height: 32}}/>
        {typeof titleElement === "string"
          ? <Typography variant="h5">{titleElement}</Typography>
          : titleElement}
      </StyledBoxRow>
    </a>
    <StyledSpan/>
    <SearchAccessory path={props.path}/>
    <StyledSpan/>
    <PracticeSelect
      practices={props.practices}
      selectedPractice={props.selectedPractice}
      setSelectedPractice={props.setSelectedPractice}
      linkPractice={null}/>
    <StyledBoxRow style={{gap: 4, alignItems: "center"}}>
      {/*{accountAccessory}*/}
      <ProviderJewels path={props.path}/>
      <NotificationsJewel path={props.path} ref={notificationsJewelRef}/>
      <Card style={{marginLeft: PD_SM, width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
        <ButtonBase
          onClick={() => BaseApp.CONTEXT.showDialog(
            null,
            () => <UserFragment uid={props.auth.currentUser.uid}/>)}>
          <img src={UserProfilePhoto(UserCache.getInstance().getCachedUser(props.auth.currentUser.uid))}
               style={{width: "100%", height: "100%"}}/>
        </ButtonBase>
      </Card> </StyledBoxRow>
  </StyledBoxRow>;
}


export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  user?: User,
  practicePage?: Page,
  showAskWyze?: boolean,
  askWyzeMode: AskWyzeViewMode,
}

export class Main extends BaseFragment<MainProps, MainState> {

  private readonly auth = getAuth();

  protected onCreateState(props?: MainProps): MainState {
    return {...super.onCreateState(props), askWyzeMode: AskWyzeViewMode.CHAT};
  }

  static nestedPaths(): PathComponent[] {
    return TabsContainer.nestedPathsFromTabs(TABS);
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    const practicePageId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
    if (practicePageId) {
      const practicePage = await Pages.getInstance().getOrLoadItem(practicePageId);
      this.setState({
        user,
        practicePage,
      });
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (!this.state.practicePage) {
      return <StyledEmpty emptyConfig={{
        title: "Almost there. You need to set up your profile",
        text: "You must complete the set up to view this page. Sign in to the myCare app to create your profile and link your practice.",
        iconUrl: "/images/complete-setup.svg",
        action: new Action("Go to myCare app", () => window.open("https://dentwyze.in/mycare", "_blank")),
      }}/>
    }
    const showTitleBar = true;
    const titleBarHeight = 40;
    const showAskWyze = this.state.showAskWyze;
    const tabsColorScheme: TabsColorScheme = "light";
    return <div
      style={{display: "flex", flexDirection: "column", width: "100dvw", height: "100dvh", overflow: "hidden"}}>
      {showTitleBar
        ? <div
          className={"DragBar"}
          style={{
            position: "fixed",
            display: "flex",
            alignItems: "center",
            paddingLeft: PD_XLG,
            paddingRight: PD_XLG,
            width: "100%",
            height: titleBarHeight,
            flexShrink: 0,
            flexGrow: 0,
            gap: 24,
            background: COLOR_PRIMARY,
            color: this.theme.palette.primary.contrastText,
            zIndex: 1000,
          }}>
          <Typography style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingLeft: PD_XXXLG,
            paddingRight: PD_XXXLG,
            fontWeight: "bold", fontSize: "100%", width: "100%",
            textAlign: "center"
          }}>
            {`${App.CONTEXT.getAppConfig().name + getPracticePageNameTitleSuffix()}`}
          </Typography>
        </div>
        : null}
      <div style={{position: "relative", marginTop: titleBarHeight, display: "flex", flexGrow: 1, overflow: "hidden"}}>
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
          <StyledBoxRow style={{
            padding: PD_MD,
            alignItems: "center",
            height: 72,
            justifyContent: "center",
            background: this.theme.palette.background.default,
            // background: `linear-gradient(${theme.palette.grey[100]},${theme.palette.grey[300]})`,
            borderBottom: DIVIDER,
          }}>
            <MainTitleBar
              auth={this.auth}
              path={this.props.path}
              practices={[this.state.practicePage.practicePageProfile]}
              selectedPractice={this.state.practicePage.practicePageProfile}
              setSelectedPractice={null}/>
          </StyledBoxRow>
          <AppTabsContainer
            path={this.props.path}
            noLogo={true}
            noUserButton={true}
            tabs={TABS}
            tabsThemeColor={this.theme.palette.grey[tabsColorScheme as string === "light" ? 100 : 900]}
            tabsColorScheme={tabsColorScheme}
          />
        </div>
        {Features.ASKWYZE_ENABLED && !showAskWyze
          ? <div
            style={{
              position: "absolute",
              width: 40,
              height: SZ_MD,
              right: 0,
              bottom: SZ_MD,
              overflow: "hidden",
            }}>
            <Button
              onClick={() => this.setState({showAskWyze: true})}
              style={{
                zIndex: 1000,
                padding: `0 ${PD_SM}px 0 ${PD_XXSM}px`,
                minWidth: 0,
                width: 50,
                height: "100%",
              }}
              variant={"contained"}>
              <img src={"/images/wyze_icon_d.svg"}
                   style={{width: "100%", height: "100%", objectFit: "contain"}}/>
            </Button>
          </div>
          : null}
        {Features.ASKWYZE_ENABLED && showAskWyze
          ? <div style={{
            display: "flex",
            flexDirection: "column",
            width: PAGE_FRAGMENT_HALF_WIDTH,
            flexShrink: 0,
            borderLeft: DIVIDER
          }}>
            <StyledBoxRow style={{
              width: "100%",
              height: SZ_MD,
              padding: PD_SM,
              borderBottom: DIVIDER,
              alignItems: "center"
            }}>
              <Typography variant={"h6"}>Ask Wyze</Typography>
              <StyledSpan/>
              <IconButton
                onClick={() => {
                  this.setState({askWyzeMode: AskWyzeViewMode.NEW_CHAT});
                }}>
                <AddOutlined/>
              </IconButton>
              <IconButton
                onClick={() => {
                  this.setState({showAskWyze: false});
                  this.props.path.navigate("/messages");
                }}>
                <OpenInFullOutlined/>
              </IconButton>
              <IconButton
                onClick={() => this.setState({showAskWyze: false})}>
                <CloseOutlined/>
              </IconButton>
            </StyledBoxRow>
            <AskWyzeFragment
              mode={this.state.askWyzeMode}
              setMode={mode => this.setState({askWyzeMode: mode})}/>
          </div>
          : null}
      </div>
    </div>;
  }
}
